/*
 * Task 03 — Robust lazy-image reveal
 * ----------------------------------
 * The theme hides lazy images with `.lazy-image img.is-loading { opacity: 0.000001 }`
 * and only reveals them by removing the `is-loading` class. That removal depends on a
 * single `document.addEventListener('load', ..., true)` capture listener in theme.js,
 * which is unreliable in Safari/iOS (and on slow networks) for images that load AFTER
 * the initial paint — so below-the-fold images stay invisible (white boxes).
 *
 * This file guarantees every `.lazy-image img.is-loading` becomes visible by binding a
 * reliable per-element load/error handler, sweeping already-complete images, watching
 * for dynamically injected images (apps, AJAX paging), and a pure-CSS failsafe.
 *
 * Additive & self-contained. To remove: delete this file and its <script> include in
 * layout/theme.liquid.
 */
(function () {
  'use strict';

  var LOADING = 'is-loading';
  var SEL = 'img.' + LOADING;

  // --- CSS failsafe: if this script (or the theme's reveal) never runs, no image is
  // left permanently invisible. Generous delay so it only matters as a last resort. ---
  try {
    var style = document.createElement('style');
    style.setAttribute('data-mos-task03', '');
    style.textContent =
      '@keyframes mosImgFailsafe{to{opacity:1}}' +
      '.lazy-image img.is-loading{animation:mosImgFailsafe 0s linear 10s forwards}';
    (document.head || document.documentElement).appendChild(style);
  } catch (e) {}

  function reveal(img) {
    if (!img || !img.classList || !img.classList.contains(LOADING)) return;
    img.classList.remove(LOADING);
    var p = img.parentNode;
    if (p && p.classList) {
      p.classList.remove(LOADING);
      var gp = p.parentNode;
      if (gp && gp.classList && gp.classList.contains(LOADING)) gp.classList.remove(LOADING);
    }
  }

  function bind(img) {
    // Already finished (covers Safari's flaky document 'load' capture on first paint).
    if (img.complete && img.naturalWidth > 0) { reveal(img); return; }
    var done = function () { reveal(img); };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true }); // reveal on error too, so no stuck shimmer
  }

  function scan(root) {
    var list = (root || document).querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) bind(list[i]);
  }

  function start() {
    scan(document);

    // Safety sweep: catch any image that completed without firing our handler
    // (late third-party rewrites, cached decodes, etc.). ~10s then stop.
    var sweeps = 0;
    var timer = setInterval(function () {
      var left = document.querySelectorAll(SEL);
      for (var i = 0; i < left.length; i++) {
        if (left[i].complete && left[i].naturalWidth > 0) reveal(left[i]);
      }
      if (++sweeps >= 20 || left.length === 0) clearInterval(timer);
    }, 500);

    // Watch for images added after load (apps, AJAX collection paging, quick views).
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches(SEL)) bind(n);
            else if (n.querySelectorAll) scan(n);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
